import { isCsvFile } from 'pages/Management/Fleets/BulkAddAssetModal/utils/isCsvFile';

import { hasHeaderMismatch } from 'utils/csvHeaders';

import { findTemplateMismatch, parseResetDepotCsv } from './parseResetDepotCsv';

export const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
// Mirrors PlanDepotResetMutation::MAX_ASSET_ROWS. The server enforces its own
// copy; this one only spares the user a round trip.
export const MAX_ASSET_ROWS = 10000;

export type ResetDepotFileError =
  | 'INVALID_TYPE'
  | 'INVALID_CSV'
  | 'TOO_LARGE'
  | 'INVALID_HEADERS'
  | 'EMPTY'
  | 'TOO_MANY_ROWS';

/**
 * File-level rejections only — row-level problems (bad VIN, unmatched FMS ID,
 * duplicates) are left to the server, which reports each in the results CSV.
 * Rejecting a row client-side would drop it before the API can spare the
 * asset it names.
 */
export const validateResetDepotFile = async (
  file: File,
): Promise<ResetDepotFileError | null> => {
  if (!isCsvFile(file)) return 'INVALID_TYPE';
  if (file.size > MAX_FILE_SIZE_BYTES) return 'TOO_LARGE';

  let rows: Awaited<ReturnType<typeof parseResetDepotCsv>>;
  try {
    // Headers first: a file built from a stale template must be rejected
    // before its rows are read. Under reset semantics an unread row is an
    // asset the API is told to release, so a mismatched file is the most
    // costly kind to let through.
    if (hasHeaderMismatch(await findTemplateMismatch(file))) {
      return 'INVALID_HEADERS';
    }

    rows = await parseResetDepotCsv(file);
  } catch {
    return 'INVALID_CSV';
  }

  if (rows.length === 0) return 'EMPTY';
  if (rows.length > MAX_ASSET_ROWS) return 'TOO_MANY_ROWS';

  return null;
};
